import React, { useState } from 'react';
import { Button, Stack } from '@mui/material';
import './DateRangePicker.scss';
import arrowLeftIcon from '../../assets/images/arrow-left.png';
import arrowRightIcon from '../../assets/images/arrow-right.png';

const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange, onSubmit }) => {
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999); // end of day
  const weekAgo = new Date();
  weekAgo.setDate(currentDate.getDate() - 7);
  weekAgo.setHours(0, 0, 0, 0); // start of day

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [hoveredDate, setHoveredDate] = useState(null);

  const selectThisWeek = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust if today is Sunday
    const lastMonday = new Date(today.setDate(diff));
    lastMonday.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(); // today's date
    endOfWeek.setHours(23, 59, 59, 999);
    onStartDateChange(lastMonday);
    onEndDateChange(endOfWeek);
};

const selectLastWeek = () => {
  const today = new Date();
  const day = today.getDay();
  const diff = today.getDate() - day - 7 + (day === 0 ? -6 : 1); // Adjust if today is Sunday
  const lastMonday = new Date(today.setDate(diff));
  lastMonday.setHours(0, 0, 0, 0);
  const previousSunday = new Date(lastMonday);
  previousSunday.setDate(lastMonday.getDate() + 6);
  previousSunday.setHours(23, 59, 59, 999);
  onStartDateChange(lastMonday);
  onEndDateChange(previousSunday);
};


const selectThisMonth = () => {
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    onStartDateChange(startOfMonth);
    onEndDateChange(currentDate);
};

const selectLastMonth = () => {
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
    startOfMonth.setHours(0, 0, 0, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    onStartDateChange(startOfMonth);
    onEndDateChange(endOfMonth);
};

  const handlePreviousMonth = () => {
    setSelectedMonth((prevMonth) => {
      if (prevMonth === 0) {
        setSelectedYear((prevYear) => prevYear - 1);
        return 11;
      } else {
        return prevMonth - 1;
      }
    });
  };

  const handleNextMonth = () => {
    setSelectedMonth((prevMonth) => {
      if (prevMonth === 11) {
        setSelectedYear((prevYear) => prevYear + 1);
        return 0;
      } else {
        return prevMonth + 1;
      }
    });
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleDateClick = (date) => {
    const selectedDate = new Date(selectedYear, selectedMonth, date);
    if (!startDate || (startDate && endDate)) {
      selectedDate.setHours(0, 0, 0, 0); // start of day
      onStartDateChange(selectedDate);
      onEndDateChange(null);
    } else if (!endDate && selectedDate >= startDate) {
      selectedDate.setHours(23, 59, 59, 999); // end of day
      onEndDateChange(selectedDate);
      setDatePickerOpen(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (startDate && endDate) {
      onSubmit(startDate, endDate);
    }
  };

  const renderDatePicker = () => {
    if (datePickerOpen) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      const daysInMonth = new Date(selectedYear || currentYear, (selectedMonth !== '' ? selectedMonth : currentMonth) + 1, 0).getDate();

      const monthOptions = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];

      const yearOptions = [];
      for (let i = currentYear - 10; i <= currentYear + 10; i++) {
        yearOptions.push(i);
      }

      const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();
      const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
      const adjustedDayNames = dayNames;

      const dateInputs = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const futureDate = new Date(selectedYear, selectedMonth, i);
        const isDisabled = startDate && !endDate && futureDate < startDate;
        dateInputs.push(
          <input
            key={i}
            type="button"
            value={i}
            onClick={isDisabled ? null : () => handleDateClick(i)}
            onMouseEnter={isDisabled ? null : () => setHoveredDate(i)}
            onMouseLeave={isDisabled ? null : () => setHoveredDate(null)}
            className={
              isDisabled
                ? 'future'
                : (startDate &&
                    startDate.getDate() === i &&
                    startDate.getMonth() === selectedMonth &&
                    startDate.getFullYear() === selectedYear)
                ? 'selected'
                : (endDate &&
                    endDate.getDate() === i &&
                    endDate.getMonth() === selectedMonth &&
                    endDate.getFullYear() === selectedYear)
                ? 'selected'
                : (currentDate.getDate() === i &&
                    currentDate.getMonth() === selectedMonth &&
                    currentDate.getFullYear() === selectedYear)
                ? 'today'
                : futureDate > currentDate
                ? 'future'
                : hoveredDate === i
                ? 'stripe'
                : ''
            }
            disabled={isDisabled}
          />
        );
      }

      const selectedStartDateIndex = startDate ? startDate.getDate() - 1 : null;
      const selectedEndDateIndex = endDate ? endDate.getDate() - 1 : null;

      const stripeStartIndex = Math.min(selectedStartDateIndex, selectedEndDateIndex);
      const stripeEndIndex = Math.max(selectedStartDateIndex, selectedEndDateIndex);

      const isCurrentMonth = selectedMonth === currentDate.getMonth() && selectedYear === currentDate.getFullYear();

      const dateInputsWithStripe = dateInputs.map((input, index) => {
        if (index >= stripeStartIndex && index <= stripeEndIndex && startDate && endDate && isCurrentMonth) {
          return (
            <div className="stripe" key={index}>
              {input}
            </div>
          );
        }
        return input;
      });

      return (
        <div className="date-picker">
          <div className="date-picker-header">
            <div className="month-year">
              <button onClick={handlePreviousMonth}>
                <img src={arrowLeftIcon} alt="Previous Month" />
              </button>
              <span>{monthOptions[selectedMonth]}</span>
              <select value={selectedYear} onChange={handleYearChange}>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button onClick={handleNextMonth}>
                <img src={arrowRightIcon} alt="Next Month" />
              </button>
            </div>
          </div>
          <div className="date-picker-weekdays">
            {adjustedDayNames.map((day, index) => (
              <div key={index} className="day">
                {day}
              </div>
            ))}
          </div>
          <div className="date-picker-days">{dateInputsWithStripe}</div>
          <div className="date-picker-footer">
            {startDate && endDate && (
              <button className="close-button" onClick={() => setDatePickerOpen(false)}>
                Close
              </button>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="date-range-picker-container">
      <Stack direction="row" spacing={1} alignItems="center" className='dateRangeButtons'>
        <Button variant="outlined" color="primary" size="small" onClick={selectThisWeek}>
          This Week
        </Button>
        <Button variant="outlined" color="secondary" size="small" onClick={selectThisMonth}>
          This Month
        </Button>
        <Button variant="outlined" color="primary" size="small" onClick={selectLastWeek}>
          Last Week
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={selectLastMonth}>
          Last Month
        </Button>
      </Stack>
      <form onSubmit={handleSubmit}>
        <div className="date-input">
          <label>
            Start Date:
            <br />
            <input
              type="text"
              readOnly
              value={startDate ? startDate.toDateString() : ''}
              onClick={() => setDatePickerOpen(true)}
            />
          </label>
          <label>
            End Date:
            <br />
            <input
              type="text"
              readOnly
              value={endDate ? endDate.toDateString() : ''}
              onClick={() => setDatePickerOpen(true)}
            />
          </label>
        </div>
      </form>
      {renderDatePicker()}
    </div>
  );
};

export default DateRangePicker;
